import React, { useState, ReactElement, useMemo } from "react";

import { useParams, Link, useLocation } from "wouter";
import { Alert } from "../../../../../components/src/Alert";
import { Dropdown } from "../../../../../components/src/Dropdown";

import { YesNoModal } from "../../../../../components/src/YesNoModal";
import { Grid, Column } from "../../../../../components/src";
import { useMutation } from "urql";
import { DELETE_PARTY_EXTERNAL_ACCOUNT } from "../../../../../graphql/src/party/queries";
import { PartyPageData } from "../PartyPage";
import { Party } from "../../../types.generated";
import { externalAccountTypes, NewExternalAccountButton, ExternalAccountForm } from "../ExternalAccountForm";

export const PartyExternalAccountsTab = ({ data, refetch }: { data: PartyPageData; refetch: () => void }): ReactElement => {
    const { id: partyId, itemId } = useParams<{ id: string; tabId: string; itemId: string }>();
    const [, navigate] = useLocation();

    const [alert, setAlert] = useState(null);
    const [modal, setModal] = useState({ showModal: false, payload: null });

    const [_, deletePartyExternalAccount] = useMutation(DELETE_PARTY_EXTERNAL_ACCOUNT);

    const party: null | Partial<Party> = useMemo(() => {
        if (data.party._id === "000000000000000000000000") {
            return null;
        }
        return data.party;
    }, [data]);

    const selectExternalAccount = (accountId): void => {
        navigate(`/parties/${partyId}/externalAccounts/${accountId}`, { replace: true });
    };

    if (!party) return <div></div>;

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete external account with id " + modal.payload.externalAccountId + "?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        deletePartyExternalAccount({ partyId: modal.payload.partyId, externalAccountId: modal.payload.externalAccountId })
                            .then((result) => {
                                if ("error" in result && result.error) {
                                    setAlert(result.error.graphQLErrors[0].message);
                                } else {
                                    refetch();
                                    navigate(`/parties/${partyId}/externalAccounts`);
                                }
                            })
                            .catch((error) => {
                                setAlert(error.graphQLErrors[0].message);
                            });
                    }}
                />
            ) : null}

            <div id="partyexternalaccountsform" className="partyexternalaccountsform form mx-3">
                <div className="flex flex-wrap mb-3">
                    External account represents a netting-set like a securities external account or an ISDA agreement.
                </div>
                <div className="flex flex-wrap mx-1 mb-3 flex">
                    <div className="ms-auto">
                        <NewExternalAccountButton page={`parties/${partyId}/externalAccounts`} />
                    </div>
                </div>
                {alert ? (
                    <div className="flex flex-wrap -mx-2">
                        <div className="w-full px-2 mb-4">
                            <Alert style={{ marginTop: "10px" }} variant="danger" onClose={() => setAlert(null)} dismissible>
                                <p>{alert}</p>
                            </Alert>
                        </div>
                    </div>
                ) : null}
                <div className="flex flex-wrap -mx-2">
                    <div className={itemId ? "w-full lg:w-8/12 px-2 mb-4" : "w-full px-2 mb-4"}>
                        <Grid key={`ca-${party.externalAccounts.length}`} data={party.externalAccounts} sortable tableClassName="table-xs">
                            <Column
                                field="_id"
                                title="ID"
                                format={(field) => {
                                    return (
                                        <a
                                            href="#selectExternalAccount"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                selectExternalAccount(field);
                                            }}
                                        >
                                            {field}
                                        </a>
                                    );
                                }}
                            />
                            <Column field="name" className="grid-column-sticky" />
                            <Column
                                field="reconcile"
                                title="Reconcile"
                                format={(value) => (typeof value === "undefined" || value ? "Yes" : "No")}
                            />
                            <Column
                                field="_id"
                                title=""
                                format={(value) => {
                                    return (
                                        <Dropdown>
                                            <Dropdown.Toggle variant="danger" size="sm">
                                                Delete
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    as="button"
                                                    onClick={async () => {
                                                        setModal({
                                                            showModal: true,
                                                            payload: { externalAccountId: value, partyId: partyId }
                                                        });
                                                    }}
                                                >
                                                    Delete external account
                                                </Dropdown.Item>
                                                <Dropdown.Item>Cancel</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    );
                                }}
                            />
                        </Grid>
                    </div>

                    {itemId ? (
                        <div className="w-full lg:w-4/12 px-2 mb-4">
                            <Link className="close-btn float-right" to={`/parties/${partyId}/externalAccounts`} />
                            <ExternalAccountForm
                                key={itemId}
                                id={itemId.startsWith("new") ? null : itemId}
                                cid={partyId}
                                type={
                                    itemId.startsWith("new")
                                        ? externalAccountTypes.find((d) => d.toLowerCase() === itemId.substring(3, itemId.length))
                                        : null
                                }
                                onCreate={() => {
                                    refetch();
                                }}
                                onUpdate={() => {
                                    refetch();
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
